import { Network } from '@capacitor/network'
import { supabase } from './supabase'

export type NetworkStatus = 'online' | 'offline'
export type ConnectionType = 'wifi' | 'cellular' | 'none' | 'unknown'

interface OfflineAction {
  id: string
  type: 'create' | 'update' | 'delete'
  table: string
  data: any
  timestamp: number
}

const OFFLINE_QUEUE_KEY = 'cartrip_offline_queue'
const CACHED_DATA_KEY = 'cartrip_cached_data'
const LAST_SYNC_KEY = 'cartrip_last_sync'

let networkStatus: NetworkStatus = 'online'
let connectionType: ConnectionType = 'unknown'
let statusCallbacks: Array<(status: NetworkStatus) => void> = []

/**
 * Initialize network monitoring
 */
export async function initializeNetworkMonitoring(): Promise<void> {
  try {
    // Get initial network status
    const status = await Network.getStatus()
    updateNetworkStatus(status.connected ? 'online' : 'offline', status.connectionType)
    
    // Listen for network status changes
    Network.addListener('networkStatusChange', (status) => {
      updateNetworkStatus(status.connected ? 'online' : 'offline', status.connectionType)
      
      // If we just came back online, sync offline queue
      if (status.connected) {
        console.log('📡 Connection restored, syncing offline queue...')
        syncOfflineQueue()
      }
    })
    
    console.log('✓ Network monitoring initialized')
  } catch (error) {
    console.error('Failed to initialize network monitoring:', error)
  }
}

/**
 * Update network status and notify listeners
 */
function updateNetworkStatus(status: NetworkStatus, type: ConnectionType): void {
  const previousStatus = networkStatus
  networkStatus = status
  connectionType = type
  
  console.log(`📡 Network status: ${status} (${type})`)
  
  // Notify all callbacks if status changed
  if (previousStatus !== status) {
    statusCallbacks.forEach(callback => callback(status))
  }
}

/**
 * Subscribe to network status changes
 */
export function onNetworkStatusChange(callback: (status: NetworkStatus) => void): () => void {
  statusCallbacks.push(callback)
  
  // Return unsubscribe function
  return () => {
    statusCallbacks = statusCallbacks.filter(cb => cb !== callback)
  }
}

/**
 * Get current network status
 */
export function getNetworkStatus(): { status: NetworkStatus; type: ConnectionType } {
  return { status: networkStatus, type: connectionType }
}

/**
 * Check if device is online
 */
export function isOnline(): boolean {
  return networkStatus === 'online'
}

/**
 * Add action to offline queue
 */
export async function queueOfflineAction(
  type: 'create' | 'update' | 'delete',
  table: string,
  data: any
): Promise<void> {
  const queue = getOfflineQueue()
  
  const action: OfflineAction = {
    id: crypto.randomUUID(),
    type,
    table,
    data,
    timestamp: Date.now()
  }
  
  queue.push(action)
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue))
  
  console.log(`📥 Action queued for offline sync:`, action)
}

/**
 * Get offline queue
 */
export function getOfflineQueue(): OfflineAction[] {
  try {
    const queue = localStorage.getItem(OFFLINE_QUEUE_KEY)
    return queue ? JSON.parse(queue) : []
  } catch (error) {
    console.error('Error reading offline queue:', error)
    return []
  }
}

/**
 * Clear offline queue
 */
function clearOfflineQueue(): void {
  localStorage.removeItem(OFFLINE_QUEUE_KEY)
}

/**
 * Sync offline queue with server
 */
export async function syncOfflineQueue(): Promise<void> {
  if (!isOnline()) {
    console.log('⚠️  Cannot sync: Device is offline')
    return
  }
  
  const queue = getOfflineQueue()
  
  if (queue.length === 0) {
    console.log('✓ No offline actions to sync')
    return
  }
  
  console.log(`🔄 Syncing ${queue.length} offline actions...`)
  
  let successCount = 0
  let failedActions: OfflineAction[] = []
  
  for (const action of queue) {
    try {
      await executeOfflineAction(action)
      successCount++
    } catch (error) {
      console.error(`Failed to sync action ${action.id}:`, error)
      failedActions.push(action)
    }
  }
  
  // Update queue with failed actions only
  if (failedActions.length > 0) {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(failedActions))
    console.log(`⚠️  ${failedActions.length} actions failed to sync`)
  } else {
    clearOfflineQueue()
  }
  
  // Update last sync timestamp
  localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString())
  
  console.log(`✓ Synced ${successCount} of ${queue.length} offline actions`)
}

/**
 * Execute a single offline action
 */
async function executeOfflineAction(action: OfflineAction): Promise<void> {
  const { type, table, data } = action
  
  switch (type) {
    case 'create':
      const { error: createError } = await supabase.from(table).insert(data)
      if (createError) throw createError
      break
      
    case 'update':
      const { id, ...updateData } = data
      const { error: updateError } = await supabase
        .from(table)
        .update(updateData)
        .eq('id', id)
      if (updateError) throw updateError
      break
      
    case 'delete':
      const { error: deleteError } = await supabase
        .from(table)
        .delete()
        .eq('id', data.id)
      if (deleteError) throw deleteError
      break
  }
}

/**
 * Cache data locally for offline access
 */
export function cacheData(key: string, data: any): void {
  try {
    const cache = getCachedData()
    cache[key] = {
      data,
      timestamp: Date.now()
    }
    localStorage.setItem(CACHED_DATA_KEY, JSON.stringify(cache))
  } catch (error) {
    console.error('Error caching data:', error)
  }
}

/**
 * Get cached data
 */
export function getCachedDataByKey(key: string, maxAge?: number): any | null {
  try {
    const cache = getCachedData()
    const cached = cache[key]
    
    if (!cached) return null
    
    // Check if data is too old
    if (maxAge && Date.now() - cached.timestamp > maxAge) {
      return null
    }
    
    return cached.data
  } catch (error) {
    console.error('Error reading cached data:', error)
    return null
  }
}

/**
 * Get all cached data
 */
function getCachedData(): Record<string, { data: any; timestamp: number }> {
  try {
    const cache = localStorage.getItem(CACHED_DATA_KEY)
    return cache ? JSON.parse(cache) : {}
  } catch (error) {
    console.error('Error reading cache:', error)
    return {}
  }
}

/**
 * Clear all cached data
 */
export function clearCache(): void {
  localStorage.removeItem(CACHED_DATA_KEY)
  console.log('✓ Cache cleared')
}

/**
 * Clear old cached data (older than maxAge ms)
 */
export function clearOldCache(maxAge: number = 7 * 24 * 60 * 60 * 1000): void {
  const cache = getCachedData()
  const now = Date.now()
  let clearedCount = 0
  
  Object.keys(cache).forEach(key => {
    if (now - cache[key].timestamp > maxAge) {
      delete cache[key]
      clearedCount++
    }
  })
  
  localStorage.setItem(CACHED_DATA_KEY, JSON.stringify(cache))
  console.log(`✓ Cleared ${clearedCount} old cache entries`)
}

/**
 * Get last sync timestamp
 */
export function getLastSyncTime(): Date | null {
  const timestamp = localStorage.getItem(LAST_SYNC_KEY)
  return timestamp ? new Date(timestamp) : null
}

/**
 * Get cache size in bytes
 */
export function getCacheSize(): number {
  const cached = localStorage.getItem(CACHED_DATA_KEY)
  return cached ? new Blob([cached]).size : 0
}

/**
 * Get offline queue size
 */
export function getOfflineQueueSize(): number {
  return getOfflineQueue().length
}

/**
 * Get offline mode statistics
 */
export function getOfflineStats(): {
  queueSize: number
  cacheSize: number
  cacheSizeFormatted: string
  lastSync: Date | null
  isOnline: boolean
  connectionType: ConnectionType
} {
  const cacheSize = getCacheSize()
  
  return {
    queueSize: getOfflineQueueSize(),
    cacheSize,
    cacheSizeFormatted: formatBytes(cacheSize),
    lastSync: getLastSyncTime(),
    isOnline: isOnline(),
    connectionType
  }
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}
